import { useCallback, useEffect, useRef, useState } from 'react';

export interface PaceContext {
  eventID: string;
  roundNumber: number;
  teeTime: string;
  scoreCode: string | null;
  totalHoles: number;
}

export interface PendingScoreUpdate {
  key: string;
  eventScoreID: string;
  roundIndex: number;
  holeIndex: number;
  holeScore: any;
  userID: string;
  enqueuedAt: string;
  paceContext?: PaceContext;
}

export type EnqueueItem = Omit<PendingScoreUpdate, 'key' | 'enqueuedAt' | 'userID'>;

const STORAGE_KEY_PREFIX = 'momentGolf-ls-pending-';
const RETRY_INTERVAL_MS = 15000;

interface UsePendingScoreQueueOptions {
  /** Ref to the axios instance — updated each render so the interceptor always has a fresh token. */
  apiRef: React.MutableRefObject<any>;
  /** Ref to the auth context — updated each render so userID is always current. */
  authRef: React.MutableRefObject<any>;
  scoreCode: string;
}

export function usePendingScoreQueue({ apiRef, authRef, scoreCode }: UsePendingScoreQueueOptions) {
  const [queue, setQueue] = useState<Map<string, PendingScoreUpdate>>(new Map());
  const queueRef = useRef<Map<string, PendingScoreUpdate>>(new Map());
  /** Tracks an in-progress flush so concurrent callers can await the same promise. */
  const activeFlush = useRef<Promise<void> | null>(null);
  const scoreCodeRef = useRef(scoreCode);

  useEffect(() => {
    scoreCodeRef.current = scoreCode;
  }, [scoreCode]);

  // --- Persistence ---

  const persistQueue = useCallback((map: Map<string, PendingScoreUpdate>) => {
    const sc = scoreCodeRef.current;
    if (!sc) return;
    try {
      const items = Array.from(map.values());
      const key = STORAGE_KEY_PREFIX + sc;
      if (items.length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(items));
      }
    } catch (err) {
      console.error('[PendingQueue] persist failed', err);
    }
  }, []);

  // Rehydrate from localStorage whenever the score code changes (e.g. after app restart).
  useEffect(() => {
    if (!scoreCode) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PREFIX + scoreCode);
      if (raw) {
        const items: PendingScoreUpdate[] = JSON.parse(raw);
        const map = new Map<string, PendingScoreUpdate>(items.map(i => [i.key, i]));
        queueRef.current = map;
        setQueue(new Map(map));
        console.log('[PendingQueue] ♻️  rehydrated from localStorage', {
          scoreCode,
          count: map.size,
          keys: Array.from(map.keys()),
        });
      } else {
        console.log('[PendingQueue] no pending items in localStorage for', scoreCode);
      }
    } catch (err) {
      console.error('[PendingQueue] rehydrate failed', err);
    }
  }, [scoreCode]);

  // --- Core flush logic ---

  /**
   * Iterates through all pending items and POSTs each one individually.
   * Stops on the first failure so items remain in the queue for the next retry.
   */
  const runFlush = useCallback(async () => {
    if (queueRef.current.size === 0) return;
    // Snapshot the queue so additions during the flush don't affect this pass.
    const snapshot = Array.from(queueRef.current.entries());
    console.log('[PendingQueue] 🚀 flush starting', {
      itemCount: snapshot.length,
      keys: snapshot.map(([k]) => k),
    });
    let successCount = 0;
    for (const [key, item] of snapshot) {
      try {
        console.log('[PendingQueue] ➡️  posting item', {
          key,
          eventScoreID: item.eventScoreID,
          roundIndex: item.roundIndex,
          holeIndex: item.holeIndex,
          playerScore: item.holeScore?.playerScore,
          markerScore: item.holeScore?.markerScore,
          userID: item.userID,
          enqueuedAt: item.enqueuedAt,
        });
        await apiRef.current.post('/ls/UpdateScore', {
          userID: item.userID,
          eventScores: [{
            eventScoreID: item.eventScoreID,
            roundIndex: item.roundIndex,
            holeIndex: item.holeIndex,
            holeScore: item.holeScore,
          }],
          ...(item.paceContext ? { paceContext: item.paceContext } : {}),
        });
        queueRef.current.delete(key);
        setQueue(new Map(queueRef.current));
        persistQueue(queueRef.current);
        successCount++;
        console.log('[PendingQueue] ✅ item posted', { key, remaining: queueRef.current.size });
      } catch (err) {
        console.warn('[PendingQueue] ❌ post failed — stopping flush, will retry in 15s', { key, err });
        break;
      }
    }
    console.log('[PendingQueue] flush complete', {
      succeeded: successCount,
      remaining: queueRef.current.size,
    });
  }, [apiRef, persistQueue]);

  /**
   * Public flush function. If a flush is already running, awaits it before
   * potentially starting another pass (handles concurrent callers like handleFinish).
   */
  const flushQueue = useCallback(async (): Promise<void> => {
    if (activeFlush.current) {
      console.log('[PendingQueue] flush already in progress — joining existing flush');
      await activeFlush.current;
      if (queueRef.current.size > 0) {
        console.log('[PendingQueue] items remain after joined flush — starting another pass');
        return flushQueue();
      }
      return;
    }
    activeFlush.current = runFlush().finally(() => {
      activeFlush.current = null;
    });
    return activeFlush.current;
  }, [runFlush]);

  // --- Enqueue ---

  /**
   * Adds or overwrites items in the queue (last write wins per hole key),
   * persists to localStorage, and triggers an immediate flush attempt.
   */
  const enqueue = useCallback((items: EnqueueItem[]) => {
    const userID =
      authRef.current?.user?.profile?.sub || `scoreCode:${scoreCodeRef.current}`;
    const now = new Date().toISOString();
    const newMap = new Map(queueRef.current);

    items.forEach(item => {
      const key = `${item.eventScoreID}:${item.roundIndex}:${item.holeIndex}`;
      newMap.set(key, { ...item, key, userID, enqueuedAt: now });
    });

    queueRef.current = newMap;
    setQueue(new Map(newMap));
    persistQueue(newMap);

    console.log('[PendingQueue] 📥 enqueued', {
      added: items.length,
      total: newMap.size,
      newKeys: items.map(i => `${i.eventScoreID}:${i.roundIndex}:${i.holeIndex}`),
    });

    // Attempt immediate flush (fire-and-forget); the 15s interval handles retries on failure.
    console.log('[PendingQueue] triggering immediate flush after enqueue');
    void flushQueue();
  }, [apiRef, authRef, flushQueue, persistQueue]);

  // --- Retry interval ---

  useEffect(() => {
    const interval = setInterval(() => {
      if (queueRef.current.size > 0) {
        console.log('[PendingQueue] ⏱️  15s retry interval fired — pending items:', queueRef.current.size, Array.from(queueRef.current.keys()));
        void flushQueue();
      }
    }, RETRY_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [flushQueue]);

  return {
    enqueue,
    flushQueue,
    pendingCount: queue.size,
  };
}
